import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

const PASSWORD_RECOVERY_REQUIRED_KEY = "lpu_password_recovery_required";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  passwordRecoveryRequired: boolean;
  markPasswordRecoveryRequired: () => void;
  clearPasswordRecoveryRequired: () => void;
  signUp: (email: string, password: string, username?: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [passwordRecoveryRequired, setPasswordRecoveryRequired] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(PASSWORD_RECOVERY_REQUIRED_KEY) === "true";
  });

  const markPasswordRecoveryRequired = useCallback(() => {
    window.localStorage.setItem(PASSWORD_RECOVERY_REQUIRED_KEY, "true");
    setPasswordRecoveryRequired(true);
  }, []);

  const clearPasswordRecoveryRequired = useCallback(() => {
    window.localStorage.removeItem(PASSWORD_RECOVERY_REQUIRED_KEY);
    setPasswordRecoveryRequired(false);
  }, []);

  useEffect(() => {
    let currentUserId: string | null = null;

    const fetchAdmin = async (userId: string) => {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin")
        .eq("user_id", userId)
        .single();
      setIsAdmin(data?.is_admin ?? false);
    };

    const applySession = (nextSession: Session | null, isInitial = false) => {
      const nextUserId = nextSession?.user?.id ?? null;

      if (isInitial || nextUserId !== currentUserId) {
        setLoading(true);
      }

      currentUserId = nextUserId;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (!nextSession?.user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Defer Supabase calls to avoid deadlocking the auth client
      // (critical on Android — awaiting inside onAuthStateChange hangs all queries).
      setLoading(false);
      setTimeout(() => {
        void fetchAdmin(nextSession.user.id);
      }, 0);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (event === "PASSWORD_RECOVERY") {
        markPasswordRecoveryRequired();
      }

      if (event === "SIGNED_IN" && window.localStorage.getItem(PASSWORD_RECOVERY_REQUIRED_KEY) !== "true") {
        clearPasswordRecoveryRequired();
      }

      if (event === "SIGNED_OUT") {
        clearPasswordRecoveryRequired();
      }

      applySession(nextSession);
    });

    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      applySession(nextSession, true);
    });

    return () => subscription.unsubscribe();
  }, [clearPasswordRecoveryRequired, markPasswordRecoveryRequired]);

  const signUp = async (email: string, password: string, username?: string) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: username?.trim(),
          display_name: username?.trim(),
        },
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) throw error;

    // Supabase celowo zwraca "sukces" dla już zarejestrowanego maila
    // (obfuskacja — nie zdradza, czy konto istnieje). Wykrywamy to po
    // pustej liście identities i pokazujemy użytkownikowi prawdziwy błąd.
    if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      throw new Error("Konto z tym adresem e-mail już istnieje. Zaloguj się lub zresetuj hasło.");
    }
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        isAdmin,
        passwordRecoveryRequired,
        markPasswordRecoveryRequired,
        clearPasswordRecoveryRequired,
        signUp,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
